import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';


const ContactCustomerModal = ({ isOpen, onClose, transaction }) => {
  const [contactMethod, setContactMethod] = useState('email');
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  if (!isOpen || !transaction) return null;

  const handleSendMessage = async () => {
    setIsSending(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setSuccessMessage(`Message sent successfully via ${contactMethod} to ${transaction?.customer}`);
    setMessage('');
    setIsSending(false);
    
    // Auto-close after success
    setTimeout(() => {
      setSuccessMessage('');
      onClose();
    }, 2000);
  };

  const contactMethods = [
    { value: 'email', label: 'Email', icon: 'Mail' },
    { value: 'sms', label: 'SMS', icon: 'MessageSquare' },
    { value: 'phone', label: 'Phone Call', icon: 'Phone' }
  ];

  const quickMessages = [
    'Your transaction has been completed successfully.',
    'We need additional information regarding your recent transaction.',
    'Your recipient has received the funds.',
    'Please provide your updated contact information.'
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-lg shadow-xl max-w-xl w-full max-h-[90vh] flex flex-col">
        <div className="p-4 border-b border-border flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Contact Customer</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Transaction: {transaction?.id}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <div className="p-4 space-y-4 overflow-y-auto flex-1">
          {successMessage ? (
            <div className="bg-success/10 border border-success/20 rounded-lg p-4">
              <div className="flex items-center space-x-2 text-success">
                <Icon name="CheckCircle" size={20} />
                <span className="font-medium">{successMessage}</span>
              </div>
            </div>
          ) : (
            <>
              <div className="bg-muted/50 rounded-lg p-3 space-y-2">
                <h3 className="text-sm font-semibold text-foreground">Customer Information</h3>
                <div className="grid grid-cols-2 gap-4 text-xs">
                  <div>
                    <div className="text-muted-foreground">Name</div>
                    <div className="font-medium text-foreground">{transaction?.customer}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Customer ID</div>
                    <div className="font-medium text-foreground">{transaction?.customerId}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Email</div>
                    <div className="font-medium text-foreground">{transaction?.customer?.toLowerCase()?.replace(' ', '.')}@email.com</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Phone</div>
                    <div className="font-medium text-foreground">+1 (555) 123-4567</div>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">Contact Method</label>
                <div className="grid grid-cols-3 gap-2">
                  {contactMethods?.map((method) => (
                    <button
                      key={method?.value}
                      onClick={() => setContactMethod(method?.value)}
                      className={`flex flex-col items-center justify-center p-3 rounded-lg border-2 transition-all ${
                        contactMethod === method?.value
                          ? 'border-primary bg-primary/10' :'border-border bg-background hover:bg-muted'
                      }`}
                    >
                      <Icon 
                        name={method?.icon} 
                        size={24} 
                        className={contactMethod === method?.value ? 'text-primary' : 'text-muted-foreground'}
                      />
                      <span className={`text-xs mt-1 ${
                        contactMethod === method?.value ? 'text-primary font-medium' : 'text-foreground'
                      }`}>
                        {method?.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">Quick Messages</label>
                <div className="space-y-1.5">
                  {quickMessages?.map((msg, index) => (
                    <button
                      key={index}
                      onClick={() => setMessage(msg)}
                      className="w-full text-left px-3 py-2 text-xs rounded-lg border border-border bg-background hover:bg-muted transition-colors"
                    >
                      {msg}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">Message</label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e?.target?.value)}
                  placeholder="Type your message here..."
                  rows={3}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                />
                <div className="text-xs text-muted-foreground text-right">
                  {message?.length}/500 characters
                </div>
              </div>
            </>
          )}
        </div>

        <div className="p-4 border-t border-border flex justify-end space-x-3 flex-shrink-0">
          <Button variant="outline" onClick={onClose} disabled={isSending}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSendMessage}
            disabled={!message?.trim() || isSending || successMessage}
          >
            {isSending ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Sending...
              </>
            ) : (
              <>
                <Icon name="Send" size={16} className="mr-2" />
                Send Message
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ContactCustomerModal;